import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { fetchAffiliateCommissions, fetchAffiliateOverview } from '@/api/affiliate';
import { formatBalanceAmount, formatBalanceU } from '@/utils/product-display';

const mockAffiliate = {
  referralCode: '',
  referralLink: '',
  totalInvited: 0,
  activeUsers: 0,
  totalEarned: '0.00 U',
  pendingEarned: '0.00 U',
  commissionRate: '10%',
};

const steps = [
  { step: '01', icon: 'ri-share-line', title: '分享专属链接', desc: '将您的专属推广链接或邀请码分享给好友' },
  { step: '02', icon: 'ri-user-add-line', title: '好友注册下单', desc: '好友通过您的链接注册并完成订单' },
  { step: '03', icon: 'ri-money-cny-circle-line', title: '获得佣金奖励', desc: '订单完成后，按后台配置比例自动返佣' },
];

function buildFullLink(value: string) {
  if (!value) return '';
  if (value.startsWith('http')) return value;
  if (value.startsWith('/')) return `${window.location.origin}${value}`;
  return `${window.location.origin}/register?invite=${value}`;
}

export default function AffiliatePage() {
  const [copied, setCopied] = useState<'code' | 'link' | null>(null);
  const [affiliate, setAffiliate] = useState(mockAffiliate);
  const [commissionRecords, setCommissionRecords] = useState<any[]>([]);

  useEffect(() => {
    fetchAffiliateOverview().then((res) => {
      if (res.code === 0 && res.data) {
        setAffiliate({
          referralCode: res.data.referralCode || '',
          referralLink: buildFullLink(res.data.referralLink || res.data.referralCode || ''),
          totalInvited: Number(res.data.totalInvited || 0),
          activeUsers: Number(res.data.activeUsers || 0),
          totalEarned: formatBalanceU(res.data.totalEarned),
          pendingEarned: formatBalanceU(res.data.pendingEarned),
          commissionRate: res.data.commissionRate || '10%',
        });
      }
    });
    fetchAffiliateCommissions().then((res) => {
      if (res.code === 0 && Array.isArray(res.data)) {
        setCommissionRecords(res.data);
      }
    });
  }, []);

  const handleCopy = (type: 'code' | 'link') => {
    const text = type === 'code' ? affiliate.referralCode : referralDisplayLink;
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  const referralDisplayLink = affiliate.referralLink || (affiliate.referralCode ? buildFullLink(affiliate.referralCode) : '');

  return (
    <div className="min-h-screen flex flex-col bg-[#f7f7f8]">
      <Navbar />
      <main className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 py-8 pb-24 md:pb-8">

        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-400 mb-6">
          <Link to="/account" className="hover:text-gray-700 transition-colors cursor-pointer">用户中心</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-gray-700 font-medium">加盟计划</span>
        </div>

        {/* Hero Banner */}
        <div className="bg-gray-900 rounded-2xl p-6 mb-5 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-56 h-56 rounded-full bg-white/5 -translate-y-1/3 translate-x-1/4" />
          <div className="absolute bottom-0 left-0 w-40 h-40 rounded-full bg-white/5 translate-y-1/3 -translate-x-1/4" />
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg">
                <i className="ri-team-line text-white text-lg" />
              </div>
              <span className="text-white/70 text-sm font-medium">推广加盟计划</span>
            </div>
            <h1 className="text-2xl font-bold text-white mb-1">邀请好友，持续赚钱</h1>
            <p className="text-white/60 text-sm mb-1">
              好友注册立即获得3次免费机器人查询机会，人工订单查询最高获得30%返佣!
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              {[
                { label: '已邀请', value: affiliate.totalInvited, unit: '人' },
                { label: '活跃用户', value: affiliate.activeUsers, unit: '人' },
                { label: '累计收益', value: affiliate.totalEarned, unit: '' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-white text-xl font-bold">{stat.value}{stat.unit}</div>
                  <div className="text-white/50 text-xs mt-0.5">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Referral Code & Link */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-5">
          <h2 className="text-base font-semibold text-gray-800 mb-4">我的推广信息</h2>
          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <div className="flex-1 min-w-0">
                <div className="text-xs text-gray-400 mb-0.5">专属邀请码</div>
                <div className="text-base font-bold text-gray-900 tracking-widest">{affiliate.referralCode || '-'}</div>
              </div>
              <button
                onClick={() => handleCopy('code')}
                className="flex items-center gap-1.5 px-3 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className={copied === 'code' ? 'ri-check-line' : 'ri-file-copy-line'} />
                {copied === 'code' ? '已复制' : '复制'}
              </button>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <div className="flex-1 min-w-0">
                <div className="text-xs text-gray-400 mb-0.5">专属推广链接</div>
                <div className="text-sm text-gray-700 truncate">{referralDisplayLink || '-'}</div>
              </div>
              <button
                onClick={() => handleCopy('link')}
                className="flex items-center gap-1.5 px-3 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className={copied === 'link' ? 'ri-check-line' : 'ri-file-copy-line'} />
                {copied === 'link' ? '已复制' : '复制'}
              </button>
            </div>
          </div>
        </div>

        {/* How it works */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-5">
          <h2 className="text-base font-semibold text-gray-800 mb-4">如何赚取佣金</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {steps.map((s) => (
              <div key={s.step} className="flex flex-col items-center text-center p-4 rounded-xl bg-gray-50">
                <div className="w-12 h-12 flex items-center justify-center bg-gray-900 rounded-xl mb-3">
                  <i className={`${s.icon} text-white text-xl`} />
                </div>
                <div className="text-xs text-gray-400 font-mono mb-1">STEP {s.step}</div>
                <div className="text-sm font-semibold text-gray-800 mb-1">{s.title}</div>
                <div className="text-xs text-gray-500 leading-relaxed">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Pending earnings */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="text-sm text-gray-500 mb-1">待结算佣金</div>
              <div className="text-2xl font-bold text-gray-900">{affiliate.pendingEarned}</div>
              <div className="text-xs text-gray-400 mt-1">订单完成后即时进入钱包余额，可申请提现</div>
            </div>
            <Link
              to="/withdraw"
              className="flex items-center justify-center gap-1.5 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-exchange-dollar-line" />
              申请提现
            </Link>
          </div>
        </div>

        {/* Commission Records */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <h2 className="text-base font-semibold text-gray-800 mb-4">佣金记录</h2>
          <div className="space-y-0">
            {commissionRecords.length === 0 && (
              <div className="py-8 text-center text-sm text-gray-400">暂无佣金记录</div>
            )}
            {commissionRecords.map((rec, idx) => (
              <div
                key={rec.commissionId}
                className={`flex items-center justify-between py-3.5 ${idx < commissionRecords.length - 1 ? 'border-b border-gray-50' : ''}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-amber-50 rounded-xl flex-shrink-0">
                    <i className="ri-gift-line text-amber-500" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-800">
                      用户#{rec.invitedUserId || '-'} · 订单返佣
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">{rec.createTime}</div>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-sm font-bold text-emerald-600">+{formatBalanceAmount(rec.amount)} U</div>
                  <div className={`text-xs mt-0.5 ${rec.status === '1' ? 'text-gray-400' : 'text-amber-500'}`}>
                    {rec.status === '1' ? '已结算' : '待结算'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

      </main>
      <Footer />
    </div>
  );
}
